import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { isLeader, isOwner } from 'entities';

import { Alert, Button, Column } from 'components/Blocks';

import { useUser } from 'hooks';
import { useProjectProfile } from '../context';

export function ProjectAlerts() {
  const loggedUser = useUser();

  const {
    project,
    projectModalConfig: { openModal },
    openTask,
  } = useProjectProfile();

  const { t } = useLang();

  const isProjectOwner = isOwner(project, loggedUser);

  const alerts = useMemo(() => {
    if (!project) return [];

    return [
      {
        action: () => openModal('MANAGE_COSTS'),

        actionText: t('view'),

        text: t('your-repricing-request-has-been-answered'),

        render: [
          project?.reprice === 'PENDING_APPROVAL',
          isProjectOwner,
          project.repriceOrigin ? project.repriceOrigin === 'EXECUTION' : true,
        ].every(Boolean),
      },

      {
        text: t('active-change-team-request-on-this-project'),

        render: [
          project?.teamChangeRequest,
          loggedUser.team.id === project?.teamChangeRequest?.toTeam ? !isLeader(loggedUser) : true,
        ].every(Boolean),
      },

      {
        text: t('to-team-change-cta'),

        action: () => openModal('CHANGE_TEAM'),

        actionText: t('review'),

        render: [
          project?.teamChangeRequest,
          loggedUser.team.id === project?.teamChangeRequest?.toTeam && isLeader(loggedUser),
        ].every(Boolean),
      },

      {
        text: t('reprice-currently-active-by-invoice'),

        render: [
          isProjectOwner || (project.owner.team === loggedUser.team.id && isLeader(loggedUser)),
          project.repriceOrigin === 'INVOICE',
        ].every(Boolean),
      },

      {
        action: () => openModal('ADD_COUNTRY'),
        actionText: t('view'),
        text: t('your-country-request-has-been-answered'),
        render: project?.addCountryRequest?.status === 'WAITING_APPROVAL' && isProjectOwner,
      },

      ...project.tasks.map(({ dateChangeRequest, name, id }) => ({
        action: () => openTask(id),
        actionText: 'Open Task',
        text: `${t('a-date-change-was-requested-on')} ${name}`,
        render: !!dateChangeRequest && isProjectOwner,
      })),
    ].filter(({ render }) => render);
  }, [isProjectOwner, openModal, project, openTask, t, loggedUser]);

  if (!alerts.length) return null;

  return (
    <Column className="animate-in slide-in-from-bottom-12" alignItems="center">
      {alerts.map(({ action, actionText, text }) => (
        <Alert level="warning" key={text} bodyClassName="flex gap-2 items-center">
          {text}

          {!!action && (
            <Button
              className="ml-auto"
              color="warning"
              variant="ghost"
              size="small"
              onClick={action}
            >
              {actionText}
            </Button>
          )}
        </Alert>
      ))}
    </Column>
  );
}
